import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector, useDispatch } from 'react-redux';
import dogIconInactive from '../../assets/dog-selected.svg';
import catIconInactive from '../../assets/cat-selected.svg';
import loadingSpinner from '../../assets/loading-spinner.json';
import doneSpinner from '../../assets/loading-check-white.json';

import { PetType } from '../../constants';
import { useOnEscEvent, useSavedElement } from '../../hooks';
import { saveSelectedRemovePetModal, removePet } from '../../actions';
import { forceFocusElement } from '../../util';

import FigoLottie from '../common/FigoLottie';
import './ModalRemovePet.css';

const ModalRemovePet = ({ isSecondStep = false, ...rest }) => {
  const { t } = useTranslation('quoting');
  const store = useSelector((stores) => stores.quoting);
  const {
    data,
    petQuoteSelected,
    petRemovedLoading,
    petRemovedSuccess,
    petToRemove,
  } = store;
  const { petName, petType } = petToRemove;
  const dispatch = useDispatch();
  const { goToSavedElementAndForgot } = useSavedElement();

  const description = isSecondStep
    ? t('removePetModal.descriptionInYourInfo', { petName })
    : t('removePetModal.descriptionInCustomize', { petName });

  const focusDropdown = (newQuoteId) => () => {
    forceFocusElement(`pet-${newQuoteId}`);
  };

  const handleRemovePetClick = () => {
    if (petRemovedLoading) {
      return;
    }

    const petQuoteList = data.ebPetQuoteResponseList
      .filter((element) => element.petQuoteId !== petQuoteSelected);
    const newPetQuoteSelected = petQuoteList[
      petQuoteList.length - 1].petQuoteId;
    const newQuoteSelectedValues = store.quoteSelectedValues
      .filter((element) => element.quoteId !== petQuoteSelected);

    const {
      parameters,
      guidData,
      sessionInformation: { nopCommerceUser, userEmail },
    } = store;

    const petSelected = petQuoteList.find((pet) => (
      pet.petQuoteId === newPetQuoteSelected));

    dispatch(removePet({
      diamondClientId: nopCommerceUser.DiamonClientdId,
      ebGuID: guidData.GuID,
      effectiveDateCustom: store.effectiveDateCustom,
      eMail: userEmail || parameters.email,
      employerName: guidData.Name,
      guidPayload: guidData,
      modifiers: petSelected.modifiers,
      nopCommerceClientId: nopCommerceUser.CustomerNopCommerceId,
      parameters,
      petQuoteList,
      promoCode: data.groupCode,
      quoteId: newPetQuoteSelected,
      quoteSelectedValues: newQuoteSelectedValues,
      removePetIds: [petQuoteSelected],
      toggleOpen: focusDropdown(newPetQuoteSelected),
    }));
  };

  const handleCancelRemovePetClick = useCallback(() => {
    dispatch(saveSelectedRemovePetModal(false));
    setTimeout(goToSavedElementAndForgot, 0.01);
  }, [dispatch, goToSavedElementAndForgot]);

  useEffect(() => {
    if (rest.registerCloseHandler) {
      rest.registerCloseHandler(handleCancelRemovePetClick);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCancelRemovePetClick]);

  useOnEscEvent(() => {
    handleCancelRemovePetClick();
  }, []);

  function renderContentButton() {
    if (!petRemovedLoading && !petRemovedSuccess) {
      return t('removePetModal.remove');
    }

    const animationData = petRemovedLoading ? loadingSpinner : doneSpinner;

    return (
      <FigoLottie
        animationData={animationData}
        height={30}
        width={30}
      />
    );
  }

  return (
    <div className="Modal-remove-pet-container">
      <img
        alt=""
        aria-hidden="true"
        className="Modal-remove-pet-image"
        src={petType === PetType.Dog.value
          ? dogIconInactive : catIconInactive}
      />

      <h4 className="Modal-remove-pet-title">{t('removePetModal.title')}</h4>

      <span className="Modal-remove-pet-description">{description}</span>

      <div className="Modal-remove-footer-buttons-container">
        <button
          className={'Modal-remove-footer-button '
            + 'Modal-remove-footer-cancel-button'}
          disabled={petRemovedLoading}
          onClick={handleCancelRemovePetClick}
          type="button"
        >
          {t('removePetModal.cancel')}
        </button>

        <button
          className={'Modal-remove-footer-button '
            + 'Modal-remove-footer-remove-button'}
          data-isloading={petRemovedLoading}
          onClick={handleRemovePetClick}
          type="button"
        >
          {renderContentButton()}
        </button>
      </div>
    </div>
  );
};

export default ModalRemovePet;
